import { FlexLayout, H4, Icon } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import type { IDefinitionContainer, IDefinitionItem } from '@deity-io/di-data-definitions';
import { ListDefinition, isIDefinitionContainer } from '@deity-io/di-data-definitions';
import { Input, Modal } from '@deity-io/portal-ui';
import SchemaView from 'components/SchemaEditor/SchemaView';
import { Tooltip } from 'components/Tooltip';
import type { FC } from 'react';
import { useRef, useState } from 'react';

interface SchemaPathPickerProps {
  isOpen?: boolean;
  isEditable?: boolean;
  property: string;
  label?: string;
  inputLabel?: string;
  description?: string;
  value: string | undefined;
  schema?: IDefinitionItem;
  onChange?: (key: string, val: string) => void;
  onBlur?: (key: string, val: string) => void;
  onSelect?: (key: string, val: string) => void;
  type?: 'schema' | 'object';
  pathSeparator?: string;
  placeholder?: string;
  sx?: IPropertyBag;
  isHeaderHidden?: boolean;
  error?: boolean;
}

export const SchemaPathPicker: FC<SchemaPathPickerProps> = ({
  isOpen = false,
  isEditable = true,
  property,
  value,
  schema,
  description,
  onChange,
  onBlur,
  onSelect,
  type = 'schema',
  pathSeparator = '/',
  placeholder,
  label,
  inputLabel,
  sx,
  isHeaderHidden = false,
  error = false
}) => {
  const [openState, setOpenState] = useState(isOpen);
  const [selectedField, setSelectedField] = useState<Array<string>>();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const fromObjNotation = (inputPath: Array<string>): Array<string> | undefined => {
    const output: Array<string> = [];
    if (schema) {
      let cursor: IDefinitionItem | IDefinitionContainer | Array<IDefinitionItem | IDefinitionContainer> = schema;
      const success = inputPath.every((part, partIndex) => {
        if (!part.length) {
          return true;
        }
        if (Array.isArray(cursor)) {
          // Todo : overloads
          return false;
        }
        if (!isIDefinitionContainer(cursor)) {
          // cant get a child of a field
          return false;
        }
        let newCursor;
        if (ListDefinition.isItMe(cursor)) {
          // todo, get byIndex?
          newCursor = cursor.items.first();
        } else {
          newCursor = cursor.items.get(part);
        }
        if (!newCursor) {
          return false;
        }
        output.push(newCursor.key);
        cursor = newCursor;

        return true;
      });
      if (success) {
        return output;
      }
    }
  };

  const toObjNotation = (inputValue: Array<string>): Array<string> => {
    const output: Array<string> = [];
    if (schema) {
      let cursor: IDefinitionItem | IDefinitionContainer | Array<IDefinitionItem | IDefinitionContainer> = schema;
      const success = inputValue.every((part, partIndex) => {
        if (Array.isArray(cursor)) {
          // Todo : overloads
          return false;
        }
        if (!isIDefinitionContainer(cursor)) {
          // cant get a child of a field
          return false;
        }
        const newCursor = cursor.items.get(part);
        if (!newCursor) {
          return false;
        }
        if (ListDefinition.isItMe(cursor)) {
          // todo, get byIndex?
          output.push('0');
        } else if (Array.isArray(newCursor)) {
          output.push(newCursor[0].key);
        } else {
          output.push(newCursor.key);
        }
        cursor = newCursor;

        return true;
      });
      if (success) {
        return output;
      }
    }

    return [];
  };

  const formatFn =
    type === 'schema'
      ? (inputValue?: Array<string>) => (inputValue ? `${pathSeparator}${inputValue.join(pathSeparator)}` : '')
      : (inputValue?: Array<string>) =>
          inputValue ? `${pathSeparator}${toObjNotation(inputValue).join(pathSeparator)}` : '';

  let actualSelectedField = selectedField;
  if (!selectedField && value && value.length > 0) {
    // detect the initial selected field
    if (type !== 'schema') {
      actualSelectedField = fromObjNotation(value.split(pathSeparator));
    }
  }
  const currentValue = formatFn(actualSelectedField);

  return (
    <FlexLayout p="xs" mt="xs" mb="xs" bg="panelLight" borderRadius="none" css={{ minHeight: '78px' }} width="full">
      <FlexLayout alignItems="center" width="full">
        {isHeaderHidden || (
          <FlexLayout width="full" alignItems="center" justifyContent="space-between">
            <H4 fontWeight="bold" mr="xs" style={{ textTransform: 'capitalize' }}>
              {property}
            </H4>
            {description && (
              <Tooltip content={description} direction="left">
                <Icon src="helpOutline" color="secondary" stroke="transparent" />
              </Tooltip>
            )}
          </FlexLayout>
        )}
        <FlexLayout
          p="xs"
          mt="xs"
          mb="xs"
          bg="panelLight"
          // sx={{ flexGrow: 1 }}
          borderRadius="small"
          width="full"
          // itemAlign="center"
        >
          {label && (
            <FlexLayout alignItems="center" width="full">
              <FlexLayout width="full" alignItems="center" justifyContent="space-between">
                <H4 fontWeight="bold" mr="xs" style={{ textTransform: 'capitalize' }}>
                  {label}
                </H4>
                {description && (
                  <Tooltip content={description} direction="left">
                    <Icon src="helpOutline" color="secondary" stroke="transparent" />
                  </Tooltip>
                )}
              </FlexLayout>
            </FlexLayout>
          )}
          <FlexLayout width="full" flexDirection="row" alignItems="center">
            <Input
              sx={{ flexGrow: 1 }}
              key="SchemaPathPickerInput"
              inputRef={inputRef}
              type="text"
              disabled={!isEditable}
              defaultValue={value as string | number | undefined | readonly string[]}
              placeholder={placeholder}
              label={inputLabel}
              autoComplete="off"
              error={error}
              onChange={e => {
                if (inputRef.current && onChange) {
                  const inputValue = inputRef.current.value;
                  onChange(property, inputValue);
                }
              }}
              onBlur={e => {
                if (inputRef.current && onBlur) {
                  const inputValue = inputRef.current.value;
                  onBlur(property, inputValue);
                }
              }}
            />
            {isEditable && (
              <Icon
                key="SchemaPathPickerIcon"
                src="edit"
                // color="secondary"
                stroke="transparent"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setOpenState(true);
                }}
              />
            )}
          </FlexLayout>

          {openState && (
            <Modal
              isOpen={openState}
              onClose={() => {
                setOpenState(false);
                setSelectedField(undefined);
              }}
              label="Select a field"
              title={currentValue.length ? currentValue : '-'}
              confirmText="Select"
              closeIcon={<Icon src="close" />}
              onConfirm={() => {
                if (onChange) {
                  if (inputRef.current) {
                    inputRef.current.value = currentValue;
                  }
                  onChange(property, currentValue);
                }
                if (onSelect) {
                  if (inputRef.current) {
                    inputRef.current.value = currentValue;
                  }
                  onSelect(property, currentValue);
                }
                setOpenState(false);
                setSelectedField(undefined);
              }}
            >
              {schema && (
                <SchemaView
                  field={schema.key}
                  selectedField={actualSelectedField}
                  onSelect={setSelectedField}
                  schema={schema.toJSON()}
                />
              )}
            </Modal>
          )}
        </FlexLayout>
      </FlexLayout>
    </FlexLayout>
  );
};
